import { CalendarDate, GregorianCalendar, getLocalTimeZone } from "@internationalized/date";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { useDateField } from "@react-aria/datepicker";
import { type DateFieldStateOptions, useDateFieldState } from "@react-stately/datepicker";
import { clsx, mergeRefs, type WithClassName } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import { lazy, Suspense, useCallback, useId, useMemo, useRef, useState } from "react";
import type { Matcher } from "react-day-picker";
import { RegrelloButton } from "../../action/button/RegrelloButton";
import { buttonVariants } from "../../action/button/regrelloButtonUtils";
import { RegrelloSpinner } from "../../feedback/spinner/RegrelloSpinner";
import {
  RegrelloPopoverAnchor,
  RegrelloPopoverContent,
  RegrelloPopoverRoot,
  RegrelloPopoverTrigger,
} from "../../overlay/popover/RegrelloPopover";
import type { RegrelloIntent } from "../../tokens/enums/regrelloIntent";
import { RegrelloIcon } from "../../visual/icons/RegrelloIcon";
import { getInputV2ButtonSizeForInputSize, INPUT_V2_DEFAULT_SIZE } from "../input/inputV2Utils";
import { RegrelloInput, type RegrelloInputProps } from "../input/RegrelloInput";
import { useSeedFirstSegment } from "./hooks/useSeedFirstSegment";
import { RegrelloDateSegment } from "./RegrelloDateSegment";

const RegrelloCalendar = lazy(async () => {
  // eslint-disable-next-line lingui/no-unlocalized-strings
  const component = await import("./RegrelloCalendar");
  return { default: component.RegrelloCalendar };
});

export interface RegrelloDatePickerProps
  extends Pick<DateFieldStateOptions, "maxGranularity">,
    Pick<RegrelloInputProps, "autoFocus" | "fullWidth" | "size" | "disabled" | "inputRef" | "onKeyDownCapture">,
    WithClassName {
  /** Default value for uncontrolled input */
  defaultValue?: Date;

  /**
   * When true, the calendar picker will be dynamically shown and hidden based on the focus of the
   * input element. This should be enabled when the date picker is used in a table cell.
   *
   * If enabled by setting to true:
   * - There will be no button to open or close the calendar popover.
   * - The calendar picker will be shown when the input is focused.
   * - The calendar picker will be hidden when the input is blurred.
   * - The calendar picker will be hidden and the input blurred when the user selects a date via the
   *   picker.
   *
   * @default false
   */
  enableDynamicCalendarPicker?: boolean;

  /**
   * The initial keystroke to apply to the first segment. This is used to seed the first segment
   * when the input is focused.
   *
   * This is useful in the case of a date cell in an AG Grid, where the first keystroke mounts this
   * component and then must be passed through.
   */
  firstSegmentInitialKeystroke?: string | null;

  /**
   * Ref to the leftmost date segment.
   *
   * Allows the input to be programmatically focused if the input is missing a value when a form is
   * submitted.
   */
  firstSegmentRef?: React.Ref<HTMLElement>;

  /** Apply the disabled modifier to the matching days. */
  getIsDateDisabled?: (date: Date) => boolean;

  /**
   * Wheather to hide `clear date` button.
   *
   * @default false
   */
  hideClearButton?: boolean;

  /**
   * The semantic intent of this input.
   *
   * @default RegrelloIntent.NEUTRAL
   */
  intent?: Extract<RegrelloIntent, "neutral" | "danger" | "warning">;

  /** Accessible name for the input */
  label?: string;

  /** Maximum selectable date */
  maxValue?: Date;

  /** Minimum selectable date */
  minValue?: Date;

  /**
   * Change handler for the controlled value. Must be set along `value` property. `reasonIfNull`
   * will be set to "clear" if the value was explicitly cleared, or "invalid" if the user is
   * currently typing a year that is incomplete (<1000).
   */
  onChange?: (date: Date | string | null, reasonIfNull: "clear" | "invalid" | undefined) => void;

  /**
   * Callback invoked when the popover is closed.
   */
  onClosePopover?: () => void;

  /**
   * A custom group id to manage interactions with the date picker and popups. If none is provided,
   * a new group id will be generated.
   *
   * This allows for proper focus group handling across the field, popup, and clear button.
   *
   * One use case is when the date picker is used in a table cell and API logic in a parent
   * component needs to know this group id.
   */
  overrideFocusGroupId?: string;

  /** Currently selected controlled value. Must be set along `onChange` property. */
  value?: Date | string | null;

  /**
   * Should chosen value be represented as string instead of `Date`
   */
  valueAsString?: boolean;
}

export type RegrelloDatePickerImperativeHandle = {
  /** Focuses the first segment in the date input. */
  focus: () => void;
};

/**
 * This component provides tokenized date input with the calendar popover.
 */
export function RegrelloDatePicker({
  className,
  defaultValue,
  disabled,
  enableDynamicCalendarPicker: shouldEnableDynamicCalendarPicker = false,
  firstSegmentRef: firstSegmentRefProp,
  firstSegmentInitialKeystroke,
  fullWidth,
  getIsDateDisabled,
  hideClearButton = false,
  inputRef,
  intent = "neutral",
  // FIXME (swerner): This is the component internal label also used for sub labels and is expected in tests. This is tricky and might need some larger refactoring. #I18NCOMPLABEL
  // eslint-disable-next-line lingui/no-unlocalized-strings
  label = "date picker",
  minValue,
  maxGranularity,
  maxValue,
  onChange,
  onClosePopover,
  onKeyDownCapture,
  overrideFocusGroupId,
  size = INPUT_V2_DEFAULT_SIZE,
  value,
  valueAsString,
  ...props
}: RegrelloDatePickerProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [internalDate, setInternalDate] = useState<CalendarDate | null>(null);

  const generatedFocusGroupId = useId();
  const focusGroupId = overrideFocusGroupId ?? generatedFocusGroupId;

  const togglePopover = useCallback(() => {
    setIsPopoverOpen((prev) => !prev);
  }, []);

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false);
    onClosePopover?.();
  }, [onClosePopover]);

  const controlledValue = useMemo(() => {
    if (value != null) {
      return toCalendarDate(isDate(value) ? value : new Date(value));
    }
    return internalDate;
  }, [internalDate, value]);

  const controlledOnChange = useCallback(
    (date: CalendarDate | null) => {
      if (date == null) {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        onChange?.(null, "clear");
      } else if (isIncompleteYear(date.year)) {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        onChange?.(null, "invalid");
      } else {
        const nativeDate = date.toDate(getLocalTimeZone());

        // (dosipiuk): Fix preventing native JS to forcefully set min year of 1900 - year bump is
        // handled in year token blur event.
        nativeDate?.setFullYear(date.year);

        if (valueAsString) {
          onChange?.(nativeDate.toISOString(), undefined);
        } else {
          onChange?.(nativeDate, undefined);
        }
      }

      setInternalDate(date);
    },
    [onChange, valueAsString],
  );

  const onDateChange = useCallback(
    (date: Date | undefined) => {
      if (onChange != null) {
        if (date == null) {
          // eslint-disable-next-line lingui/no-unlocalized-strings
          onChange(null, "clear");
        } else if (valueAsString) {
          onChange(date.toISOString(), undefined);
        } else {
          onChange(date, undefined);
        }
      }
      setInternalDate(toCalendarDate(date) || null);
    },
    [onChange, valueAsString],
  );

  const refInternal = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const firstSegmentRefInternal = useRef<HTMLElement>(null);

  useSeedFirstSegment({
    firstSegmentInitialKeystroke,
    firstSegmentRef: firstSegmentRefInternal,
  });

  const controlledDefaultValue = useMemo(() => toCalendarDate(defaultValue), [defaultValue]);
  const controlledMinValue = useMemo(() => toCalendarDate(minValue) || undefined, [minValue]);
  const controlledMaxValue = useMemo(() => toCalendarDate(maxValue) || undefined, [maxValue]);

  const state = useDateFieldState({
    createCalendar,
    defaultValue: controlledDefaultValue,
    isDisabled: disabled,
    locale: i18n.locale,
    value: controlledValue,
    onChange: controlledOnChange,
    minValue: controlledMinValue,
    maxGranularity: maxGranularity,
    maxValue: controlledMaxValue,
  });

  const { fieldProps } = useDateField(
    {
      ...props,
      isDisabled: disabled,

      // FIXME (swerner): This is not really a useful name. I guess making `label` a requirement here would be good for accessibility!
      "aria-label": label ?? t`date picker`,
    },
    state,
    refInternal,
  );

  /** Converts dates in the short format to current millenia. Ex. 22 => 2022. */
  const onYearBlur = useCallback(() => {
    if (internalDate != null && isIncompleteYear(internalDate.year)) {
      const currentMillenia = Math.floor(new Date().getFullYear() / 1000) * 1000;

      const newDate = internalDate?.toDate(getLocalTimeZone());
      newDate.setFullYear(newDate.getFullYear() + currentMillenia);

      onDateChange(newDate);
    }
  }, [internalDate, onDateChange]);

  const calendarSelectedDate = controlledValue?.toDate(getLocalTimeZone());

  return (
    <RegrelloPopoverRoot
      onOpenChange={(nextOpenState) => {
        if (!shouldEnableDynamicCalendarPicker && !nextOpenState) {
          buttonRef.current?.focus();
        }
      }}
      open={isPopoverOpen}
    >
      <RegrelloPopoverAnchor asChild={true}>
        <RegrelloInput
          ref={inputRef}
          asChild={true}
          className={clsx(className, "pr-0", {
            "pl-1": size === "small" || size === "medium",
            "pl-1.5": size === "large",
            "pl-2": size === "x-large",
          })}
          disabled={disabled}
          endElement={{
            type: "interactive",
            element: (
              <div className="flex items-center gap-0.5" data-focus-group={focusGroupId}>
                {hideClearButton ? null : (
                  <RegrelloButton
                    // FIXME (swerner): The aria-label should be localized and use the 'label' prop correctly
                    aria-label="clear date, date picker"
                    className={clsx({
                      // (jullman): Observation and note for the future: We have the ability to
                      // "hideClearButton" which removes it entirely and the invisible formatting
                      // space is lost and we also have this invisible prop that we use to show and
                      // hide the clear button while preserving the empty space that it will appear
                      // in. However, This "invisible" state causes that area to be not clickable,
                      // and in fact registers as a click outside of this group, bluring the date
                      // field element. TODO: Figure out a better way to handle this.
                      invisible: controlledValue == null,
                      // HACKHACK (clewis): Custom button sizes tuned especially for fitting within this input:
                      "w-4 h-4": size === "x-small",
                      "w-5 h-5": size === "small",
                    })}
                    iconOnly={true}
                    onClick={() => {
                      onDateChange(undefined);
                    }}
                    onMouseDown={(event) => {
                      // (jullman): Prevent the focus being moved to the clear button and then lost.
                      // It would be lost because the clear button will set to null in this click as
                      // the date is cleared.
                      event.preventDefault();
                    }}
                    size={getInputV2ButtonSizeForInputSize(size)}
                    startIcon="close"
                    variant="ghost"
                  />
                )}
                {shouldEnableDynamicCalendarPicker ? null : (
                  <RegrelloPopoverTrigger asChild={true} onClick={togglePopover}>
                    {size === "x-small" ? (
                      // (clewis): Use custom, super-tiny button to fit in the borders of the input.
                      <button
                        ref={buttonRef}
                        // FIXME (swerner): The aria-label should be localized and use the 'label' prop correctly
                        aria-label="open calendar, date picker"
                        className={clsx(buttonVariants({ variant: "ghost" }), "w-4 h-4 px-px")}
                        data-testid={DataTestIds.FORM_FIELD_DATE_CALENDAR_BUTTON}
                        disabled={disabled}
                        type="button"
                      >
                        <RegrelloIcon iconName="date-field" intent="neutral" size="x-small" />
                      </button>
                    ) : size === "small" ? (
                      <button
                        ref={buttonRef}
                        // FIXME (swerner): The aria-label should be localized and use the 'label' prop correctly
                        aria-label="open calendar, date picker"
                        className={clsx(buttonVariants({ variant: "ghost" }), "mt-0.75 p-0.5")}
                        data-testid={DataTestIds.FORM_FIELD_DATE_CALENDAR_BUTTON}
                        disabled={disabled}
                        type="button"
                      >
                        <RegrelloIcon iconName="date-field" intent="neutral" size="x-small" />
                      </button>
                    ) : (
                      <RegrelloButton
                        ref={buttonRef}
                        // FIXME (swerner): The aria-label should be localized and use the 'label' prop correctly
                        aria-label="open calendar, date picker"
                        dataTestId={DataTestIds.FORM_FIELD_DATE_CALENDAR_BUTTON}
                        disabled={disabled}
                        iconOnly={true}
                        size={getInputV2ButtonSizeForInputSize(size)}
                        startIcon="date-field"
                        variant="ghost"
                      />
                    )}
                  </RegrelloPopoverTrigger>
                )}
              </div>
            ),
          }}
          fullWidth={fullWidth}
          intent={intent}
          size={size}
          {...props}
        >
          <div
            ref={refInternal}
            className="flex items-center"
            data-focus-group={focusGroupId}
            {...fieldProps}
            onFocus={(event) => {
              fieldProps.onFocus?.(event);
              if (shouldEnableDynamicCalendarPicker && !disabled) {
                setIsPopoverOpen(true);
              }
            }}
          >
            {state.segments.map((segment, i) => (
              <RegrelloDateSegment
                key={i}
                ref={i === 0 ? mergeRefs(firstSegmentRefInternal, firstSegmentRefProp) : undefined}
                intent={intent}
                onBlur={onYearBlur}
                segment={segment}
                size={size}
                state={state}
              />
            ))}
          </div>
        </RegrelloInput>
      </RegrelloPopoverAnchor>

      <RegrelloPopoverContent
        ref={popoverContentRef}
        align="end"
        className="w-auto min-w-82 min-h-87"
        data-focus-group={focusGroupId}
        omitDefaultPadding={true}
        onOpenAutoFocus={(event) => {
          if (shouldEnableDynamicCalendarPicker) {
            // (jullman): In dynamic mode, focusing the date field opens the calendar picker which
            // draws the focus by default. Prevent default to keep focus on the field input.
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          const target = event.target as HTMLElement | null;
          const isInSameGroup = focusGroupId != null && target?.closest(`[data-focus-group="${focusGroupId}"]`) != null;
          if (shouldEnableDynamicCalendarPicker && isInSameGroup) {
            return;
          }
          closePopover();
        }}
      >
        <Suspense fallback={<RegrelloSpinner />}>
          <RegrelloCalendar
            autoFocus={!shouldEnableDynamicCalendarPicker}
            defaultMonth={calendarSelectedDate}
            disabled={getIsDateDisabled}
            endMonth={maxValue}
            hidden={{ before: minValue, after: maxValue } as Matcher}
            mode="single"
            onSelect={(date) => {
              onDateChange(date);
              if (shouldEnableDynamicCalendarPicker) {
                closePopover();
              } else {
                buttonRef.current?.click();
              }
            }}
            selected={calendarSelectedDate}
            startMonth={minValue}
          />
        </Suspense>
      </RegrelloPopoverContent>
    </RegrelloPopoverRoot>
  );
}

// (dosipiuk): This is here to treeshake alternative calendar implementations.
function createCalendar(identifier: string) {
  switch (identifier) {
    case "gregory":
      return new GregorianCalendar();
    default:
      throw new Error(`Unsupported calendar ${identifier}`);
  }
}

/**
 * Converts native JS Date to custom `react-aria` implementation.
 */
function toCalendarDate(date?: Date | null) {
  if (date == null) {
    return null;
  }

  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

function isDate(date: string | Date): date is Date {
  return Object.prototype.toString.call(date) === "[object Date]";
}

function isIncompleteYear(year: number) {
  return year < 1000;
}
